/**
 * 🎨 DIVIDER FLOATING TOOLBAR 2025
 * 
 * Floating Toolbar für Divider-Element im Stil der ButtonFloatingToolbar
 * - Style Presets
 * - Brand Colors + Custom Color Slots + Color Picker
 * - Dimensions (Thickness, Width)
 * - Spacing (Margins)
 */

import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useDebouncedCallback } from '../../hooks/useDebounce';
import './DividerFloatingToolbar.css';

function DividerFloatingToolbar({
    element,
    onClose,
    onStyleChange,
    colors = {},
    anchorRef,
    isMobileLayout = false
}) {
    // 🎯 Expandable Sections State
    const [expandedSection, setExpandedSection] = useState(null); // 'presets', 'colors', 'dimensions', 'spacing'
    const toolbarRef = useRef(null);
    
    // 🎯 Dragging State
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isManuallyPositioned, setIsManuallyPositioned] = useState(false);
    const [toolbarPosition, setToolbarPosition] = useState({ top: 80, left: window.innerWidth / 2 });
    
    // 🎨 Custom Color Slots (shared with TipTap via LocalStorage)
    const [customColorSlots, setCustomColorSlots] = useState(() => {
        try {
            const stored = localStorage.getItem('tiptap-custom-color-slots');
            return stored ? JSON.parse(stored) : ['#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff'];
        } catch (error) {
            return ['#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff'];
        }
    });
    
    // 🎨 Aktuell editierter Color Slot (für Color Picker)
    const [editingSlotIndex, setEditingSlotIndex] = useState(null);

    // 🎨 Save Custom Colors to LocalStorage
    useEffect(() => {
        try {
            localStorage.setItem('tiptap-custom-color-slots', JSON.stringify(customColorSlots));
        } catch (error) {
            console.error('Failed to save custom color slots:', error);
        }
    }, [customColorSlots]);
    
    // 🎨 Sync Custom Colors beim Öffnen der Colors Section
    useEffect(() => {
        if (expandedSection === 'colors') {
            try {
                const stored = localStorage.getItem('tiptap-custom-color-slots');
                if (stored) {
                    const storedSlots = JSON.parse(stored);
                    if (JSON.stringify(storedSlots) !== JSON.stringify(customColorSlots)) {
                        setCustomColorSlots(storedSlots);
                    }
                }
            } catch (error) {
                console.error('Failed to sync custom color slots:', error);
            }
        }
    }, [expandedSection]);
    
    // 🎨 Update specific color slot
    const updateColorSlot = (index, color) => {
        const newSlots = [...customColorSlots];
        newSlots[index] = color;
        setCustomColorSlots(newSlots);
    };

    // ⚡ Performance: Debounced Firebase Save für Slider
    const debouncedStyleChange = useDebouncedCallback((updates) => {
        if (onStyleChange) {
            onStyleChange(updates);
        }
    }, 70); // 70ms = Ultra-responsive, aber trotzdem optimiert
    
    // 🎯 Drag Handlers
    const handleMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        setIsDragging(true);
        setDragOffset({
            x: e.clientX - toolbarPosition.left,
            y: e.clientY - toolbarPosition.top
        });
    };
    
    const handleMouseMove = (e) => {
        if (!isDragging) return;
        
        const newLeft = e.clientX - dragOffset.x;
        const newTop = e.clientY - dragOffset.y;
        
        // Bounds checking
        const maxLeft = window.innerWidth - (toolbarRef.current?.offsetWidth || 300);
        const maxTop = window.innerHeight - (toolbarRef.current?.offsetHeight || 200);
        
        setToolbarPosition({
            left: Math.max(0, Math.min(newLeft, maxLeft)),
            top: Math.max(0, Math.min(newTop, maxTop))
        });
    };
    
    const handleMouseUp = () => {
        setIsDragging(false);
        setIsManuallyPositioned(true);
    };
    
    // ✅ Drag Event Listeners
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDragging]);

    // ✅ Handle Outside Click
    useEffect(() => {
        const handleClickOutside = (event) => {
            const clickedInToolbar = toolbarRef.current && toolbarRef.current.contains(event.target);
            const clickedInDivider = anchorRef && anchorRef.current && anchorRef.current.contains(event.target);
            
            if (!clickedInToolbar && !clickedInDivider) {
                setExpandedSection(null);
                if (onClose) {
                    onClose();
                }
            }
        };
        
        // ✅ FIX: 'click' statt 'mousedown' - schließt NUR bei echtem Click!
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [onClose]);

    // ✅ Close on Escape
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                if (expandedSection) {
                    setExpandedSection(null);
                } else {
                    onClose();
                }
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [onClose, expandedSection]);

    // Divider Style Presets
    const presets = [
        { id: 'simple-line', name: 'Simple', icon: '─' },
        { id: 'gradient-line', name: 'Gradient', icon: '═' },
        { id: 'dashed-line', name: 'Dashed', icon: '┄' },
        { id: 'dotted-line', name: 'Dotted', icon: '┈' },
        { id: 'double-line', name: 'Double', icon: '═' },
        { id: 'thick-gradient', name: 'Thick', icon: '▬' },
        { id: 'elegant-shadow', name: 'Shadow', icon: '━' },
        { id: 'glow-line', name: 'Glow', icon: '▬' },
        { id: 'premium-fade', name: 'Fade', icon: '─' }
    ];

    const currentStyle = element.dividerStyle || 'simple-line';
    const currentColor = element.dividerColor || colors.accentColor || '#5C80BC';
    const currentThickness = element.dividerThickness || 2;
    const currentWidth = element.dividerWidth || 100;
    const currentWidthType = element.dividerWidthType || 'percent';
    const currentMaxWidth = element.dividerMaxWidth || 800;

    // ✅ PORTAL RENDERING - Im Stil der ButtonFloatingToolbar
    return ReactDOM.createPortal(
        <div 
            ref={toolbarRef}
            className={`divider-floating-toolbar-portal ${expandedSection ? 'expanded' : ''} ${isDragging ? 'dragging' : ''}`}
            style={{
                position: 'fixed',
                top: `${toolbarPosition.top}px`,
                left: `${toolbarPosition.left}px`,
                transform: 'translateX(-50%)',
                zIndex: 999999
            }}
        >
            <div 
                className="divider-toolbar"
                onMouseDown={(e) => {
                    // ✅ FIX: Stoppe alle mousedown Events innerhalb der Toolbar
                    // Verhindert, dass der Outside-Click-Handler triggert
                    e.stopPropagation();
                }}
                onClick={(e) => {
                    // ✅ FIX: Stoppe auch click Events (für doppelte Sicherheit)
                    e.stopPropagation();
                }}
            >
                {/* 🎨 MAIN TOOLBAR BUTTONS - oben fixiert */}
                <div className="divider-toolbar-row">
                    {/* 🎯 Drag Handle Icon */}
                    <div 
                        className="divider-drag-handle"
                        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                        title="Drag to move toolbar"
                        onMouseDown={handleMouseDown}
                        onClick={(e) => {
                            // ✅ FIX: Verhindere dass Click-Event die Toolbar schließt
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <i className="fas fa-grip-vertical"></i>
                    </div>
                    
                    {/* 1. Style Presets */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'presets' ? null : 'presets');
                        }}
                        className={`divider-toolbar-btn ${expandedSection === 'presets' ? 'is-active' : ''}`}
                        title="Divider Styles"
                    >
                        <i className="fas fa-grip-horizontal"></i>
                    </button>

                    {/* 2. Colors */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'colors' ? null : 'colors');
                        }}
                        className={`divider-toolbar-btn ${expandedSection === 'colors' ? 'is-active' : ''}`}
                        title="Divider Color"
                    >
                        <i className="fas fa-palette"></i>
                    </button>

                    {/* 3. Dimensions (Thickness + Width) */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'dimensions' ? null : 'dimensions');
                        }}
                        className={`divider-toolbar-btn ${expandedSection === 'dimensions' ? 'is-active' : ''}`}
                        title="Dimensions (Thickness & Width)"
                    >
                        <i className="fas fa-ruler-combined"></i>
                    </button>

                    {/* 4. Spacing (Margins) */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'spacing' ? null : 'spacing');
                        }}
                        className={`divider-toolbar-btn ${expandedSection === 'spacing' ? 'is-active' : ''}`}
                        title="Spacing (Margins)"
                    >
                        <i className="fas fa-arrows-alt-v"></i>
                    </button>
                </div>
                
                {/* 🎨 EXPANDABLE SECTIONS */}
                
                {/* ═══════════════════ PRESETS SECTION ═══════════════════ */}
                {expandedSection === 'presets' && (
                    <div className="divider-expanded-section">
                        <div className="divider-presets-grid">
                            {presets.map(preset => (
                                <button
                                    key={preset.id}
                                    className={`divider-preset-item ${currentStyle === preset.id ? 'active' : ''}`}
                                    onClick={() => {
                                        onStyleChange({ dividerStyle: preset.id });
                                    }}
                                    title={preset.name}
                                >
                                    <span className="divider-preset-icon">{preset.icon}</span>
                                    <span className="divider-preset-name">{preset.name}</span>
                                </button>
                            ))}
                        </div>
                        <span className="divider-section-label">Divider Styles</span>
                    </div>
                )}

                {/* ═══════════════════ COLORS SECTION ═══════════════════ */}
                {expandedSection === 'colors' && (
                    <div className="divider-expanded-section">
                        {/* 🎨 BRAND COLORS ROW */}
                        {colors && Object.keys(colors).length > 0 && (
                            <div className="divider-brand-colors">
                                {Object.values(colors).map((brandColor, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className="divider-brand-color"
                                        style={{ backgroundColor: brandColor }}
                                        onClick={() => {
                                            onStyleChange({ dividerColor: brandColor });
                                        }}
                                        title={`Brand Color ${index + 1}: ${brandColor}`}
                                    />
                                ))}
                            </div>
                        )}
                        
                        {/* 🎨 CUSTOM COLOR SLOTS - 5 fixe Slots */}
                        <div className="divider-custom-color-slots">
                            {customColorSlots.map((slotColor, slotIndex) => (
                                <button
                                    key={slotIndex}
                                    type="button"
                                    className="divider-brand-color divider-color-slot"
                                    style={{ 
                                        backgroundColor: slotColor,
                                        border: slotColor === '#ffffff' ? '2px dashed rgba(255, 255, 255, 0.3)' : '2px solid rgba(255, 255, 255, 0.3)'
                                    }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        
                                        if (slotColor !== '#ffffff') {
                                            onStyleChange({ dividerColor: slotColor });
                                        } else {
                                            setEditingSlotIndex(slotIndex);
                                            const colorInput = document.getElementById(`divider-color-slot-${slotIndex}`);
                                            if (colorInput) {
                                                colorInput.click();
                                            }
                                        }
                                    }}
                                    onContextMenu={(e) => {
                                        e.preventDefault();
                                        setEditingSlotIndex(slotIndex);
                                        const colorInput = document.getElementById(`divider-color-slot-${slotIndex}`);
                                        if (colorInput) {
                                            colorInput.click();
                                        }
                                    }}
                                    title={slotColor === '#ffffff' ? 'Click to set custom color' : `Custom Color: ${slotColor} (Right-click to edit)`}
                                >
                                    {slotColor === '#ffffff' && (
                                        <i className="fas fa-plus" style={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.5)' }}></i>
                                    )}
                                </button>
                            ))}
                            
                            {/* Versteckte Color Picker Inputs - Einer pro Slot */}
                            {customColorSlots.map((slotColor, slotIndex) => (
                                <input
                                    key={slotIndex}
                                    id={`divider-color-slot-${slotIndex}`}
                                    type="color"
                                    value={slotColor}
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        const newColor = e.target.value;
                                        updateColorSlot(slotIndex, newColor);
                                    }}
                                />
                            ))}
                        </div>
                        
                        {/* 🎨 COLOR PICKER - Current Color */}
                        <div className="divider-color-picker-wrapper">
                            <label className="divider-control-label">
                                <i className="fas fa-eyedropper"></i>
                                Color Picker
                            </label>
                            <div className="divider-color-picker">
                                <input
                                    type="color"
                                    value={currentColor}
                                    onChange={(e) => onStyleChange({ dividerColor: e.target.value })}
                                    className="divider-color-input"
                                />
                                <input
                                    type="text"
                                    value={currentColor}
                                    onChange={(e) => onStyleChange({ dividerColor: e.target.value })}
                                    className="divider-color-text"
                                    placeholder="#5C80BC"
                                />
                            </div>
                        </div>
                        
                        <span className="divider-section-label">Brand, Custom & Picker</span>
                    </div>
                )}

                {/* ═══════════════════ DIMENSIONS SECTION ═══════════════════ */}
                {expandedSection === 'dimensions' && (
                    <div className="divider-expanded-section divider-dimensions-section">
                        {/* Dicke */}
                        <div className="divider-slider-control">
                            <label className="divider-slider-label">Thickness</label>
                            <input
                                type="range"
                                min="1"
                                max="10"
                                value={currentThickness}
                                onChange={(e) => debouncedStyleChange({ dividerThickness: parseInt(e.target.value) })}
                                className="divider-slider"
                            />
                            <span className="divider-slider-value">{currentThickness}px</span>
                        </div>

                        {/* Width Type */}
                        <div className="divider-control-group">
                            <label className="divider-control-label">
                                <i className="fas fa-arrows-alt-h"></i>
                                Width Type
                            </label>
                            <div className="divider-width-type-buttons">
                                <button
                                    className={`divider-width-type-btn ${currentWidthType === 'percent' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ dividerWidthType: 'percent', dividerWidth: 100 })}
                                >
                                    <i className="fas fa-percentage"></i>
                                    Percent
                                </button>
                                <button
                                    className={`divider-width-type-btn ${currentWidthType === 'pixel' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ dividerWidthType: 'pixel', dividerWidth: 300 })}
                                >
                                    <i className="fas fa-ruler-horizontal"></i>
                                    Pixel
                                </button>
                            </div>
                        </div>

                        {/* Width */}
                        {currentWidthType === 'percent' ? (
                            <div className="divider-slider-control">
                                <label className="divider-slider-label">Width</label>
                                <input
                                    type="range"
                                    min="10"
                                    max="100"
                                    value={currentWidth}
                                    onChange={(e) => debouncedStyleChange({ dividerWidth: parseInt(e.target.value) })}
                                    className="divider-slider"
                                />
                                <span className="divider-slider-value">{currentWidth}%</span>
                            </div>
                        ) : (
                            <div className="divider-control-group">
                                <label className="divider-control-label">
                                    Width (px)
                                </label>
                                <input
                                    type="number"
                                    min="50"
                                    max="2000"
                                    value={currentWidth}
                                    onChange={(e) => debouncedStyleChange({ dividerWidth: parseInt(e.target.value) || 50 })}
                                    className="divider-number-input"
                                />
                            </div>
                        )}

                        {/* Max Width (nur bei Pixel) */}
                        {currentWidthType === 'pixel' && (
                            <div className="divider-control-group">
                                <label className="divider-control-label">
                                    Max Width (px)
                                </label>
                                <input
                                    type="number"
                                    min="100"
                                    max="2000"
                                    value={currentMaxWidth}
                                    onChange={(e) => onStyleChange({ dividerMaxWidth: parseInt(e.target.value) || 100 })}
                                    className="divider-number-input"
                                />
                            </div>
                        )}
                        
                        <span className="divider-section-label">Thickness & Width</span>
                    </div>
                )}

                {/* ═══════════════════ SPACING SECTION ═══════════════════ */}
                {expandedSection === 'spacing' && (
                    <div className="divider-expanded-section divider-spacing-section">
                        {/* Margin Top */}
                        <div className="divider-slider-control">
                            <label className="divider-slider-label">Top</label>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={isMobileLayout ? (element.marginTopMobile ?? element.marginTop ?? 20) : (element.marginTop ?? 20)}
                                onChange={(e) => {
                                    const prop = isMobileLayout ? 'marginTopMobile' : 'marginTop';
                                    onStyleChange({ [prop]: parseInt(e.target.value) });
                                }}
                                className="divider-slider"
                            />
                            <span className="divider-slider-value">
                                {isMobileLayout ? (element.marginTopMobile ?? element.marginTop ?? 20) : (element.marginTop ?? 20)}px
                            </span>
                        </div>

                        {/* Margin Bottom */}
                        <div className="divider-slider-control">
                            <label className="divider-slider-label">Bottom</label>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={isMobileLayout ? (element.marginBottomMobile ?? element.marginBottom ?? 20) : (element.marginBottom ?? 20)}
                                onChange={(e) => {
                                    const prop = isMobileLayout ? 'marginBottomMobile' : 'marginBottom';
                                    onStyleChange({ [prop]: parseInt(e.target.value) });
                                }}
                                className="divider-slider"
                            />
                            <span className="divider-slider-value">
                                {isMobileLayout ? (element.marginBottomMobile ?? element.marginBottom ?? 20) : (element.marginBottom ?? 20)}px
                            </span>
                        </div>

                        {/* Info */}
                        <div className="divider-spacing-info">
                            <i className="fas fa-info-circle"></i>
                            <span>{isMobileLayout ? 'Mobile' : 'Desktop'} spacing</span>
                        </div>
                        
                        <span className="divider-section-label">Margins</span>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

export default DividerFloatingToolbar;

